import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEffectiveAccess } from '@/api/branchRelationships';
import { PersonPicker } from '@/components/common/assignPickers';
import type { EffectiveAccessRow } from '@/types/oscar';
import { geographyLabel, sourceLabel } from './schema';
import { ErrorBanner } from './shared';

const sourceCls: Record<string, string> = {
  Role: 'bg-navy/10 text-navy',
  CostCentre: 'bg-[#E6ECF2] text-navy',
  Manual: 'bg-orange/15 text-orange',
};

/**
 * Effective access — "who can see what": oscar.vPersonBranchScope for one
 * person, exactly as insights and every other tenant read it. Source says why
 * each row is there (their role, their cost centre, or a manual row), so an
 * unexpected row can be traced back to the tab that grants it.
 */
export function EffectiveAccessTab() {
  const [personId, setPersonId] = useState('');

  const effectiveQuery = useQuery({
    queryKey: ['branch-rel', 'effective', personId],
    queryFn: ({ signal }) => getEffectiveAccess(Number(personId), signal),
    enabled: personId !== '',
  });
  // ⚠ A disabled query still returns the last person's cached data — gate on the pick.
  const data = personId !== '' ? effectiveQuery.data : undefined;

  const byModule = useMemo(() => {
    const groups = new Map<string, { title: string; rows: EffectiveAccessRow[] }>();
    for (const row of data?.Rows ?? []) {
      const g = groups.get(row.ModuleKey) ?? { title: row.ModuleTitle ?? row.ModuleKey, rows: [] };
      g.rows.push(row);
      groups.set(row.ModuleKey, g);
    }
    return [...groups.entries()];
  }, [data]);

  return (
    <div>
      <p className="text-sm text-darkGrey">
        What a person can see, per module, as every Oscar tenant reads it. No row means no access.
      </p>

      <div className="mt-3 max-w-md">
        <PersonPicker label="Person to check" value={personId} onChange={(v) => setPersonId(v)} />
      </div>

      {effectiveQuery.isLoading && personId !== '' && <p className="mt-4 text-sm text-darkGrey/70">Working it out…</p>}
      {effectiveQuery.isError && personId !== '' && (
        <ErrorBanner message={(effectiveQuery.error as Error).message || 'Could not load effective access.'} />
      )}

      {data && (
        <div className="mt-4 space-y-4">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-1 rounded border border-border bg-[#F4F6F9] p-4 text-sm sm:grid-cols-2">
            <Fact label="Person" value={`${data.Person.DisplayName} (${data.Person.PrimaryEmail})`} />
            <Fact
              label="Role"
              value={
                data.Person.DepartmentCode
                  ? `${data.Person.DepartmentCode} — ${data.Person.DepartmentDescription ?? 'not a known role'}${
                      data.Person.GeographicCode ? ` (${geographyLabel[data.Person.GeographicCode] ?? data.Person.GeographicCode})` : ''}`
                  : 'No role'
              }
            />
            <Fact
              label="Location"
              value={data.Person.LocationDesc ? `${data.Person.LocationDesc}${data.Person.LocationBranchCode ? ` (${data.Person.LocationBranchCode})` : ''}` : 'None'}
            />
            <Fact label="Cost centre" value={data.Person.CostCentreCode ?? 'None'} />
          </dl>

          {!data.Person.Active && (
            <p className="text-sm font-semibold text-orange">This person is inactive, so they have no access at all.</p>
          )}

          {byModule.length === 0 ? (
            <p className="text-sm text-darkGrey">No branch access for any module.</p>
          ) : (
            byModule.map(([key, group]) => (
              <section key={key} aria-label={group.title}>
                <h3 className="font-heading text-base font-semibold text-navy">
                  {group.title} <span className="font-mono text-xs font-normal text-darkGrey/60">{key}</span>
                </h3>
                <div className="mt-2 overflow-x-auto rounded border border-border bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-navy text-left text-white">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Scope</th>
                        <th className="px-3 py-2 font-semibold">Branch</th>
                        <th className="px-3 py-2 font-semibold">Location</th>
                        <th className="px-3 py-2 font-semibold">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((r, i) => (
                        <tr key={`${r.ScopeType}|${r.BranchCode ?? ''}|${r.Source}`} className={i % 2 ? 'bg-[#F4F6F9]' : 'bg-white'}>
                          <td className="px-3 py-2">{r.ScopeType}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.BranchCode ?? 'Every branch'}</td>
                          <td className="px-3 py-2">{r.ScopeType === 'Company' ? '—' : r.LocationDesc ?? 'Not an Oscar location'}</td>
                          <td className="px-3 py-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${sourceCls[r.Source] ?? ''}`}>
                              {sourceLabel[r.Source] ?? r.Source}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 font-semibold text-darkGrey">{label}</dt>
      <dd className="text-darkGrey">{value}</dd>
    </div>
  );
}
